import {
  Alert,
  Box,
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { DeliveryApi, PullSheetApi, VehicleApi } from "../../apis"
import type { IDeliveryRoute } from "../../apis/delivery-route"
import type { IVehicle } from "../../apis/vehicle"
import { message } from "../../utils/message"
import { renderFullName } from "../../utils/render-name"
import {
  AssignValidationChecks,
  StaffPickerList,
  VehiclePickerList,
  driverLicenseCheck,
  driverStatusCheck,
  vehicleCapacityCheck,
  type CheckRow,
  type StaffOption,
  type VehicleOption,
} from "../pending-deliveries/atoms"
import { useEditRouteAssignment } from "./hooks"
import { translateRouteError } from "./route-errors"
import { useVehicleClassLabel } from "./vehicle-class"

const VEHICLE_CLASS_TO_MAX_SIZE: Record<string, number> = {
  MOTORCYCLE: 1,
  CAR: 3,
  VAN: 5,
  TRUCK_SMALL: 6,
  TRUCK_LARGE: 12,
}

interface Props {
  opened: boolean
  onClose: () => void
  route: IDeliveryRoute
  onSuccess?: () => void
}

interface FormShape {
  driverId: string
  vehicleId: string
  pullSheetStaffId: string
}

function initialsOf(name?: string | null): string {
  if (!name) return "-"
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "-"
}

export function EditRouteDriverDialog({ opened, onClose, route, onSuccess }: Props) {
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([])
  const [staffLoading, setStaffLoading] = useState(false)

  const { t } = useTranslation(["delivery"])
  const classLabel = useVehicleClassLabel()
  const { submit, submitting, error } = useEditRouteAssignment()

  const form = useForm<FormShape>({
    initialValues: {
      driverId: route.driverId ?? "",
      vehicleId: route.vehicleId ?? "",
      pullSheetStaffId: route.pullSheetStaffId ?? "",
    },
  })

  useEffect(() => {
    if (!opened) return
    form.setValues({
      driverId: route.driverId ?? "",
      vehicleId: route.vehicleId ?? "",
      pullSheetStaffId: route.pullSheetStaffId ?? "",
    })
    let cancelled = false
    setStaffLoading(true)
    DeliveryApi.staffSelect({})
      .then((data: unknown) => {
        if (cancelled) return
        const list = Array.isArray(data)
          ? data
          : ((data as { rows?: unknown[] }).rows ?? [])
        setStaffOptions(
          (list as Array<Record<string, unknown>>).map((u) => {
            const name =
              (u.lastName || u.firstName ? renderFullName(u) : "") ||
              (u.email as string | undefined) ||
              (u.id as string)
            return {
              id: u.id as string,
              name,
              initials: initialsOf(name),
              phone: (u.phone as string | null | undefined) ?? null,
              status: (u.status as StaffOption["status"]) ?? "AVAILABLE",
              licenseExpiresAt:
                (u.driverLicenseExpiresAt as string | null | undefined) ?? null,
            }
          }),
        )
      })
      .catch(() => {
        if (!cancelled) setStaffOptions([])
      })
      .finally(() => {
        if (!cancelled) setStaffLoading(false)
      })
    return () => {
      cancelled = true
    }
  // form is a stable Mantine ref, intentionally excluded from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, route.id, route.driverId, route.vehicleId, route.pullSheetStaffId])

  const { data: vehicleData } = useSWR(
    opened ? "edit-route.vehicles" : null,
    () => VehicleApi.list({ filter: { isActive: true }, offset: { limit: 50, skip: 0 } }),
    { revalidateOnFocus: false },
  )
  const vehicles: VehicleOption[] = useMemo(() => {
    const rows: IVehicle[] = vehicleData?.rows ?? []
    return rows.map((v) => ({
      id: v.id,
      plate: v.plateNumber,
      model: v.model ?? "",
      vehicleClass: v.vehicleClass,
      vehicleClassLabel: classLabel(v.vehicleClass),
      maxSize: VEHICLE_CLASS_TO_MAX_SIZE[v.vehicleClass] ?? 0,
      status: (v as { status?: VehicleOption["status"] }).status ?? "AVAILABLE",
    }))
  }, [vehicleData?.rows, classLabel])

  // Бэлдэх ажилтан (PULL_SHEET_STAFF role) — auto-create pull sheets үүсгэхэд
  // ашиглагдана. Backend endpoint:
  //   GET /ord/pull_sheet/warehouse_staff_select
  const { data: pickerData, isLoading: pickerLoading } = useSWR(
    opened ? "edit-route.pull-sheet-staff" : null,
    () => PullSheetApi.warehouseStaffSelect({ query: "", limit: 50 }),
    { revalidateOnFocus: false },
  )
  const pickerOptions: StaffOption[] = useMemo(() => {
    const list = Array.isArray(pickerData) ? pickerData : []
    return (list as Array<Record<string, unknown>>).map((u) => {
      const name =
        (u.lastName || u.firstName ? renderFullName(u) : "") ||
        (u.email as string | undefined) ||
        (u.id as string)
      return {
        id: u.id as string,
        name,
        initials: initialsOf(name),
        phone: (u.phone as string | null | undefined) ?? null,
        status: "AVAILABLE" as const,
      }
    })
  }, [pickerData])

  const selectedDriver =
    staffOptions.find((s) => s.id === form.values.driverId) ?? null
  const selectedVehicle =
    vehicles.find((v) => v.id === form.values.vehicleId) ?? null

  // Route's required size: sum the numeric prefix of each stop's sizeCode (e.g. "05S" → 5).
  // Falls back to 0 (no minimum enforced) when stops are not embedded or have no sizeCode.
  const routeRequiredSize = useMemo(() => {
    const stops = route.stops ?? []
    return stops.reduce((sum, stop) => {
      const code = stop.dn?.sizeCode ?? null
      if (!code) return sum
      const m = /(\d+(?:\.\d+)?)/.exec(code)
      return sum + (m ? Number(m[1]) : 0)
    }, 0)
  }, [route])

  const checks: CheckRow[] = [
    driverStatusCheck(selectedDriver),
    driverLicenseCheck(selectedDriver),
    vehicleCapacityCheck(selectedVehicle, routeRequiredSize),
  ]
  // "warn" нь блок хийхгүй (жш. жолоочийн эрхийн мэдээлэл хоосон) — зөвхөн
  // "fail" submit-ийг хорино. Хатуу шаардлагатай талбарууд (жолооч, машин,
  // бэлдэх ажилтан) сонгогдсон эсэхийг тус тусад нь шалгана.
  const isValid =
    checks.every((c) => c.status !== "fail") &&
    !!form.values.driverId &&
    !!form.values.vehicleId &&
    !!form.values.pullSheetStaffId

  const handleSubmit = async (values: FormShape) => {
    if (!isValid) return
    try {
      const result = await submit({
        routeId: route.id,
        driverId: values.driverId,
        vehicleId: values.vehicleId,
        pullSheetStaffId: values.pullSheetStaffId || null,
      })
      const created = result.createdPullSheetIds.length
      if (created > 0) {
        message.success(t("delivery:route.assign.doneWithSheets", { count: created }))
      } else if (result.skippedReasons.length > 0) {
        // Pull sheet үүсэхгүй байсан ч driver/vehicle/picker хадгалагдсан.
        // Шалтгааныг warning-аар үзүүлнэ.
        message.success(t("delivery:route.assign.done"))
      } else {
        message.success(t("delivery:route.assign.done"))
      }
      onSuccess?.()
      onClose()
    } catch {
      // surfaced via error
    }
  }

  const stopCount = route.totalStops
  const routeSummary = t("delivery:route.assign.summary", {
    number: route.routeNumber,
    count: stopCount,
    date: dayjs(route.plannedDate).format("YYYY-MM-DD"),
  })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Жолооч, машин, бэлдэх ажилтан оноох"
      size="md"
      padding="lg"
    >
      <Box pos="relative" component="form" onSubmit={form.onSubmit(handleSubmit)}>
        <LoadingOverlay visible={submitting} />
        <Stack gap="md">
          <Box
            p="sm"
            bg="gray.0"
            style={{ borderRadius: "var(--mantine-radius-md)" }}
          >
            <Text size="sm" c="dimmed" tt="uppercase" fw={600} mb={2}>
              Маршрут
            </Text>
            <Text size="sm" fw={600}>
              {routeSummary}
            </Text>
          </Box>

          <Divider />

          <StaffPickerList
            title="Жолооч"
            options={staffOptions}
            selectedId={form.values.driverId || null}
            onChange={(id) => form.setFieldValue("driverId", id)}
            loading={staffLoading}
            showLicenseBadge
          />

          <VehiclePickerList
            vehicles={vehicles}
            selectedId={form.values.vehicleId || null}
            onChange={(id) => form.setFieldValue("vehicleId", id)}
            minSize={routeRequiredSize > 0 ? routeRequiredSize : undefined}
          />

          <StaffPickerList
            title="Бэлдэх ажилтан"
            options={pickerOptions}
            selectedId={form.values.pullSheetStaffId || null}
            onChange={(id) => form.setFieldValue("pullSheetStaffId", id)}
            loading={pickerLoading}
            searchPlaceholder="Нэр, утсаар хайх..."
            emptyHint="Идэвхтэй бэлдэх ажилтан олдсонгүй"
          />

          <AssignValidationChecks checks={checks} />

          {!form.values.pullSheetStaffId && (
            <Alert color="yellow" variant="light">
              Бэлдэх ажилтан оноогоогүй бол захиалга "Ачилт удирдлага"
              хэсэгт шилжихгүй.
            </Alert>
          )}

          {error && (
            <Alert color="red" variant="light">
              {translateRouteError(error)}
            </Alert>
          )}

          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={onClose} disabled={submitting}>
              Цуцлах
            </Button>
            <Button type="submit" disabled={!isValid} loading={submitting}>
              Хадгалах
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
